// The private site's wall decision (registration step 33; frontend-shell.md
// quirk 48, security-model.md › Private site). Mounted by SiteTemplateHost
// inside MemberAuthProvider, around the public <Routes> only, so /admin, the
// standalone pages and the boot gates are never walled (T7: the decision
// lives only here).
//
// On a public league (`visibility` not 'members_only') the value is the
// constant SITE_ACCESS_OPEN: no storage read, no request, no effect work.
//
// On a members-only league the value is derived during render (no effect
// decides it) from the credential the public client would send (member
// token, else staff token; services/publicAccess.ts) and what the gate said
// about it (the verdict store):
//
//   boot /tenant still loading, no verdict  → checking (a league that just
//                                             went public never flashes a form)
//   no credential                           → walled signed_out (no request)
//   no verdict yet                          → checking (one probe:
//                                             GET /public/site-access)
//   admitted                                → admitted (the site renders)
//   refused signed_out / not_member         → walled (the sign-in panel)
//   refused session_ended                   → walled; ApiClient already ended
//                                             the member session (memberApi)
//   refused not_registered                  → walled, "Finish your
//                                             registration"; the session is
//                                             kept (member-accounts.md quirk 39)
//   503 check_unavailable / network         → unavailable (Try again)
//
// A verdict comes from the probe or from any public read that answers 401
// members_only mid-session (ApiClient reports it), so the wall replaces the
// page in place. A not_registered or unavailable verdict is re-probed on the
// next navigation (someone who just finished registering, or whose network
// came back, gets in). The public caches are reset whenever admission or the
// credential changes. No timers and no intervals: nothing loops.

import { useCallback, useLayoutEffect, useEffect, useMemo, useRef, useSyncExternalStore, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useLeague } from '@/contexts/LeagueContext'
import { useMemberAuth } from '@/contexts/MemberAuthContext'
import { isMembersOnly } from '@/contexts/tenantAccess'
import { api } from '@/services/api'
import { MEMBER_TOKEN_KEY, onMemberSessionEnded } from '@/services/memberApi'
import {
  announceCredentialChange,
  clearSessionEndedNotice,
  clearVerdict,
  credentialKey,
  ensureProbe,
  getVerdict,
  hasSessionEndedNotice,
  publicCredential,
  sessionEndedNoticeKey,
  subscribeVerdicts,
  type SiteVerdict,
} from '@/services/publicAccess'
import { SITE_ACCESS_OPEN, SiteAccessContext, type SiteAccess, type SiteAccessNotice } from './context'
import { resetPublicCaches } from './resetPublicCaches'

const STAFF_TOKEN_KEY = 'mcl_admin_token'

const noopSubscribe = () => () => {}

/** Another tab signed in or out (member or staff), or a member 401 ended the session here. */
function subscribeCredentials(fn: () => void): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key === MEMBER_TOKEN_KEY || e.key === STAFF_TOKEN_KEY) fn()
  }
  window.addEventListener('storage', onStorage)
  const off = onMemberSessionEnded(fn)
  return () => {
    window.removeEventListener('storage', onStorage)
    off()
  }
}

const probe = () => api.getSiteAccess()

function needsReprobe(v: SiteVerdict | undefined): boolean {
  return !!v && ((v.kind === 'refused' && v.reason === 'not_registered') || v.kind === 'unavailable')
}

export function SiteAccessProvider({ children }: { children: ReactNode }) {
  const { league, isLoading } = useLeague()
  const priv = isMembersOnly(league)
  // Re-render on sign-in / sign-out in this tab: setMemberToken fires no
  // storage event here, so the credential snapshot is re-read on this render.
  useMemberAuth()
  const { pathname } = useLocation()

  const key = useSyncExternalStore(priv ? subscribeCredentials : noopSubscribe, () => (priv ? credentialKey(publicCredential()) : ''))
  const verdict = useSyncExternalStore(subscribeVerdicts, () => (priv && key ? getVerdict(key) ?? null : null))

  const retry = useCallback(() => {
    if (!key) return
    clearVerdict(key)
    ensureProbe(publicCredential(), probe, true)
  }, [key])

  // Derived in render; no effect decides it.
  let kind: SiteAccess['kind']
  let reason: Extract<SiteAccess, { kind: 'walled' }>['reason'] = 'signed_out'
  let notice: SiteAccessNotice = null
  if (!priv) kind = 'open'
  else if (isLoading && !verdict) kind = 'checking'
  else if (key === '') {
    kind = 'walled'
    notice = hasSessionEndedNotice() ? 'sessionEnded' : null
  } else if (!verdict) kind = 'checking'
  else if (verdict.kind === 'admitted') kind = 'admitted'
  else if (verdict.kind === 'refused') {
    kind = 'walled'
    reason = verdict.reason
    notice = reason === 'not_member' ? 'notMember' : reason === 'session_ended' || hasSessionEndedNotice() ? 'sessionEnded' : null
  } else kind = 'unavailable'

  const value = useMemo<SiteAccess>(() => {
    if (kind === 'open') return SITE_ACCESS_OPEN
    if (kind === 'walled') return { kind, reason, notice, retry }
    return { kind, retry }
  }, [kind, reason, notice, retry])

  // (a) One probe per credential.
  useEffect(() => {
    if (!priv || !key || verdict) return
    ensureProbe(publicCredential(), probe)
  }, [priv, key, verdict])

  // (b) Navigation re-probes a not_registered or unavailable verdict.
  const lastPath = useRef(pathname)
  useEffect(() => {
    if (lastPath.current === pathname) return
    lastPath.current = pathname
    if (!priv || !key || !needsReprobe(getVerdict(key))) return
    clearVerdict(key)
    ensureProbe(publicCredential(), probe, true)
  }, [pathname, priv, key])

  // (c) Admission or the credential changed: forget every public read core
  // keeps. A layout effect, so it runs before the newly mounted pages' data
  // effects (child passive effects would otherwise fetch first).
  // The "session ended" notice belongs to the credential that ended: it is
  // dropped on admission, and as soon as the credential moves on from any
  // other key (a fresh sign-in, so a later deliberate sign-out never shows
  // it). Subscribers outside this subtree (the Fan AI bubble) are told the
  // credential changed, since an in-tab sign-in or sign-out fires no event.
  const admitted = kind === 'admitted'
  const last = useRef({ admitted, key })
  useLayoutEffect(() => {
    const prev = last.current
    last.current = { admitted, key }
    if (!priv || (prev.admitted === admitted && prev.key === key)) return
    resetPublicCaches()
    if (admitted || (prev.key !== key && prev.key !== sessionEndedNoticeKey())) clearSessionEndedNotice()
    if (prev.key !== key) announceCredentialChange()
  }, [priv, admitted, key])

  return <SiteAccessContext.Provider value={value}>{children}</SiteAccessContext.Provider>
}
